import {
    type EasingFunction,
    Easing
} from "react-native";

export type MotionType = "movement" | "fade" | "loop";

export type MotionEasingType = "standard" | "linear" | "enter" | "inOut" | "exit";

export type MotionIntentType = "contentSwap" | "surfaceEnter" | "valueChange" | "surfaceExit" | "listInsert" | "listRemove" | "panelEnter" | "iconAppear" | "chartEnter" | "panelExit" | "chartDraw" | "collapse" | "iconSwap" | "expand" | "toggle" | "hover" | "press" | "stack";

export type MotionSpringType = "standard";

export type MotionDurationConstantType = {
    duration: keyof NCoreUIKit.Durations | number;
    durations: NCoreUIKit.Durations;
    isReduceMotionEnabled: boolean;
    motion?: MotionType;
};

export type MotionIntent = {
    duration: keyof NCoreUIKit.Durations;
    easing: MotionEasingType;
    motion: MotionType;
};

export type MotionSpring = {
    stiffness: number;
    damping: number;
    mass: number;
};

export type MotionTimingConstantType = {
    durations: NCoreUIKit.Durations;
    isReduceMotionEnabled: boolean;
    intent: MotionIntentType;
    motion?: MotionType;
};

export type MotionTiming = {
    easing: EasingFunction;
    duration: number;
};

export type MotionTransitionConstantType = MotionTimingConstantType & {
    properties: Array<string>;
};

export type MotionTransition = {
    transitionTimingFunction: string;
    transitionProperty: string;
    transitionDuration: string;
};

export const MOTION_PRESS_SCALE = 0.96;

export const MOTION_SURFACE_SCALE = 0.96;

export const MOTION_ICON_APPEAR_SCALE = 0.25;

export const MOTION_ICON_APPEAR_BLUR = 4;

export const MOTION_ICON_POP_SCALE = 1.12;

export const MOTION_BADGE_HIDDEN_SCALE = 0.4;

export const MOTION_EASINGS: Record<MotionEasingType, EasingFunction> = {
    standard: Easing.bezier(0.25, 0.1, 0.25, 1),
    inOut: Easing.bezier(0.42, 0, 0.58, 1),
    enter: Easing.bezier(0, 0, 0.58, 1),
    exit: Easing.bezier(0.42, 0, 1, 1),
    linear: Easing.linear
};

export const MOTION_CSS_EASINGS: Record<MotionEasingType, string> = {
    inOut: "ease-in-out",
    enter: "ease-out",
    linear: "linear",
    standard: "ease",
    exit: "ease-in"
};

export const MOTION_SPRINGS: Record<MotionSpringType, MotionSpring> = {
    standard: {
        stiffness: 320,
        damping: 34,
        mass: 1
    }
};

export const MOTION_INTENTS: Record<MotionIntentType, MotionIntent> = {
    chartDraw: {
        motion: "movement",
        duration: "long",
        easing: "inOut"
    },
    chartEnter: {
        motion: "movement",
        duration: "long",
        easing: "enter"
    },
    collapse: {
        duration: "medium",
        easing: "standard",
        motion: "movement"
    },
    contentSwap: {
        duration: "medium",
        easing: "standard",
        motion: "movement"
    },
    expand: {
        duration: "medium",
        easing: "standard",
        motion: "movement"
    },
    hover: {
        easing: "standard",
        duration: "short",
        motion: "fade"
    },
    iconAppear: {
        duration: "medium",
        easing: "enter",
        motion: "fade"
    },
    iconSwap: {
        duration: "medium",
        easing: "standard",
        motion: "movement"
    },
    listInsert: {
        duration: "medium",
        motion: "movement",
        easing: "enter"
    },
    listRemove: {
        motion: "movement",
        duration: "short",
        easing: "exit"
    },
    panelEnter: {
        duration: "medium",
        motion: "movement",
        easing: "enter"
    },
    panelExit: {
        motion: "movement",
        duration: "short",
        easing: "exit"
    },
    press: {
        motion: "movement",
        duration: "short",
        easing: "enter"
    },
    stack: {
        easing: "standard",
        motion: "movement",
        duration: "long"
    },
    surfaceEnter: {
        motion: "movement",
        duration: "short",
        easing: "enter"
    },
    toggle: {
        motion: "movement",
        duration: "short",
        easing: "enter"
    },
    valueChange: {
        duration: "medium",
        easing: "standard",
        motion: "movement"
    },
    surfaceExit: {
        duration: "short",
        easing: "exit",
        motion: "fade"
    }
};

export const getMotionDuration = ({
    isReduceMotionEnabled,
    motion = "movement",
    durations,
    duration
}: MotionDurationConstantType): number => {
    const currentDuration = typeof duration === "number" ? duration : durations[duration];

    if(!isReduceMotionEnabled) {
        return currentDuration;
    }

    if(motion === "fade") {
        return Math.min(currentDuration, durations.short);
    }

    return 0;
};

export const getMotionTiming = ({
    isReduceMotionEnabled,
    durations,
    motion,
    intent
}: MotionTimingConstantType): MotionTiming => {
    const currentIntent = MOTION_INTENTS[intent];

    return {
        easing: MOTION_EASINGS[currentIntent.easing],
        duration: getMotionDuration({
            motion: motion ?? currentIntent.motion,
            duration: currentIntent.duration,
            isReduceMotionEnabled,
            durations
        })
    };
};

export const getMotionTransition = ({
    isReduceMotionEnabled,
    properties,
    durations,
    motion,
    intent
}: MotionTransitionConstantType): MotionTransition => {
    const currentIntent = MOTION_INTENTS[intent];

    const duration = getMotionDuration({
        motion: motion ?? currentIntent.motion,
        duration: currentIntent.duration,
        isReduceMotionEnabled,
        durations
    });

    return {
        transitionTimingFunction: MOTION_CSS_EASINGS[currentIntent.easing],
        transitionProperty: properties.join(", "),
        transitionDuration: `${duration}ms`
    };
};
